import type { BaselineRevisionSummary } from '../../../api/baselineApis';

export interface BaselineSlotGroup {
  baselineIndex: number;
  baselineName: string;
  revisions: BaselineRevisionSummary[];
  latestApproved: BaselineRevisionSummary | null;
  revisionCount: number;
}

/** Revisions that should appear in the Baselines management UI. */
export function filterVisibleBaselineRevisions(
  revisions: BaselineRevisionSummary[],
): BaselineRevisionSummary[] {
  return revisions.filter((rev) => !rev.isDeleted);
}

/** Baseline slot indexes that are soft-deleted (for Compare dropdown disable). */
export function collectSoftDeletedBaselineIndices(
  revisions: BaselineRevisionSummary[],
): Set<number> {
  const deleted = new Set<number>();
  for (const rev of revisions) {
    if (rev.isDeleted && Number.isFinite(rev.baselineIndex)) {
      deleted.add(rev.baselineIndex);
    }
  }
  return deleted;
}

function slotDisplayName(revisions: BaselineRevisionSummary[], baselineIndex: number): string {
  for (const rev of revisions) {
    const name = rev.baselineName?.trim();
    if (name) return name;
  }
  return `Baseline ${baselineIndex}`;
}

function sortRevisionsDesc(revisions: BaselineRevisionSummary[]): BaselineRevisionSummary[] {
  return [...revisions].sort((a, b) => {
    if (b.revisionNumber !== a.revisionNumber) {
      return b.revisionNumber - a.revisionNumber;
    }
    const aAt = a.createdAt ? Date.parse(a.createdAt) : 0;
    const bAt = b.createdAt ? Date.parse(b.createdAt) : 0;
    return bAt - aAt;
  });
}

/**
 * Group flat revision summaries by baselineIndex for the Baselines management page.
 * Soft-deleted revisions are excluded (not shown in the list).
 */
export function groupRevisionsByBaselineIndex(
  revisions: BaselineRevisionSummary[],
): BaselineSlotGroup[] {
  const byIndex = new Map<number, BaselineRevisionSummary[]>();
  for (const rev of filterVisibleBaselineRevisions(revisions)) {
    if (!Number.isFinite(rev.baselineIndex)) continue;
    const list = byIndex.get(rev.baselineIndex) ?? [];
    list.push(rev);
    byIndex.set(rev.baselineIndex, list);
  }

  return [...byIndex.entries()]
    // Latest slot index first (newest allocations are higher indices).
    .sort((a, b) => b[0] - a[0])
    .map(([baselineIndex, list]) => {
      const revisionsSorted = sortRevisionsDesc(list);
      const latestApproved =
        revisionsSorted.find((r) => r.status === 'APPROVED') ?? null;
      return {
        baselineIndex,
        baselineName: slotDisplayName(revisionsSorted, baselineIndex),
        revisions: revisionsSorted,
        latestApproved,
        revisionCount: revisionsSorted.length,
      };
    });
}

export function formatBaselineTimestamp(raw?: string | null): string {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Date-only display for revision task start/finish (DD-MM-YYYY). */
export function formatBaselineDate(raw?: string | null): string {
  if (!raw) return '—';
  const isoDate = raw.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    return `${isoDate[3]}-${isoDate[2]}-${isoDate[1]}`;
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

export function baselineStatusLabel(status: BaselineRevisionSummary['status']): string {
  switch (status) {
    case 'DRAFT':
      return 'Draft';
    case 'SUBMITTED':
      return 'Awaiting approval';
    case 'REJECTED':
      return 'Rejected';
    case 'APPROVED':
      return 'Approved';
    default:
      return status;
  }
}

export function baselineStatusColor(
  status: BaselineRevisionSummary['status'],
): string {
  switch (status) {
    case 'DRAFT':
      return 'blue';
    case 'SUBMITTED':
      return 'yellow';
    case 'REJECTED':
      return 'red';
    case 'APPROVED':
      return 'teal';
    default:
      return 'gray';
  }
}
